import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./lib/api";
import { Clv } from "./routes/Clv";
import { Ops } from "./routes/Ops";
import { Review } from "./routes/Review";
import { SignalDetail } from "./routes/SignalDetail";
import { Signals } from "./routes/Signals";

type Tab = "signals" | "clv" | "review" | "ops";

const TABS: { id: Tab; label: string }[] = [
  { id: "signals", label: "Signály" },
  { id: "clv", label: "CLV" },
  { id: "review", label: "Párování" },
  { id: "ops", label: "Provoz" },
];

/** Minimalistické směrování. Deep link na signál potřebuje ntfy notifikace. */
function readRoute(): { tab: Tab; signalId: string | null } {
  const match = /^\/signals\/([0-9a-f-]{36})$/.exec(window.location.pathname);
  if (match) return { tab: "signals", signalId: match[1] ?? null };
  const tab = window.location.pathname.replace(/^\//, "") as Tab;
  return { tab: TABS.some((t) => t.id === tab) ? tab : "signals", signalId: null };
}

function Login({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(password);
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <h1>oddsline</h1>
      <p className="lede">Nástroj hledá kurzy, které jsou vyšší, než odpovídá férové ceně.</p>
      <form onSubmit={submit}>
        <label htmlFor="password">Heslo</label>
        <input id="password" type="password" autoComplete="current-password" autoFocus
               value={password} onChange={(e) => setPassword(e.target.value)} />
        <p style={{ marginTop: "1rem" }}>
          <button className="action" type="submit" disabled={busy || password === ""}>
            {busy ? "Přihlašuji" : "Přihlásit"}
          </button>
        </p>
      </form>
      {error && <p className="notice warn">{error}</p>}
    </main>
  );
}

export function App() {
  const [route, setRoute] = useState(readRoute);
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    const onPop = () => setRoute(readRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    api.signals({ status: "open" })
      .then(() => setAuthed(true))
      .catch((e: unknown) => setAuthed(!(e instanceof ApiError && e.status === 401)));
  }, []);

  const go = useCallback((path: string) => {
    window.history.pushState({}, "", path);
    setRoute(readRoute());
  }, []);

  if (authed === null) return <main className="login"><p className="empty">Načítám.</p></main>;
  if (!authed) return <Login onDone={() => setAuthed(true)} />;

  return (
    <div className="shell">
      <header className="masthead">
        <div className="wordmark">
          oddsline <span>value v kurzech</span>
        </div>
        <nav className="nav">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => go(`/${tab.id}`)}
                    aria-current={route.tab === tab.id && !route.signalId ? "page" : undefined}>
              {tab.label}
            </button>
          ))}
          <button onClick={() => void api.logout().then(() => setAuthed(false))}>Odhlásit</button>
        </nav>
      </header>

      <main>
        {route.signalId ? (
          <SignalDetail id={route.signalId} onBack={() => go("/signals")} />
        ) : route.tab === "signals" ? (
          <Signals onOpen={(id) => go(`/signals/${id}`)} />
        ) : route.tab === "clv" ? (
          <Clv />
        ) : route.tab === "review" ? (
          <Review />
        ) : (
          <Ops />
        )}
      </main>
    </div>
  );
}
